import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { AlertController, ToastController } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { data } from "autoprefixer";
declare let window: any;
import * as _ from "lodash";
import * as OneSignalMessagesActions from "../../store/oneSignalMessages/oneSignalMessages.actions";
import * as fromApp from "../../store/app.reducer";
import { first } from "rxjs";
import { Capacitor } from "@capacitor/core";
// Native push (ClickUp 86cbgmruv): apps built with @capacitor-firebase/messaging
// use Firebase Cloud Messaging; apps built before it still ship the OneSignal
// plugin and keep using OneSignal. The same code runs in both, so live updates
// are safe for every installed app - see usesFirebasePush().
// Browser push moves the same way, store by store: see FIREBASE_WEB_PUSH_GROUPS.
// Stores not switched yet keep the OneSignal web SDK (window.OneSignal).
import OneSignal from "onesignal-cordova-plugin";
import { FirebaseMessaging } from "@capacitor-firebase/messaging";
import { LocalNotifications } from "@capacitor/local-notifications";
import { App } from "@capacitor/app";
import { Device } from "@capacitor/device";
import { getApps, initializeApp } from "firebase/app";
import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from "firebase/messaging";
import { FirebaseBrowserService } from "../firebaseBrowser/firebase-browser.service";
import * as AuthActions from "../../auth/store/auth.actions";
import {
  disableUnavailablePushPreferences,
  PushPreferenceResult,
  supportsAndroidPushPreferences,
} from "./push-preferences";

// Android notification channel for push. Also the FCM default channel, set in
// AndroidManifest.xml by android-build-before.js, so background and foreground
// notifications behave the same. High importance so they drop down (heads-up).
const ANDROID_PUSH_CHANNEL_ID = "orders";

// Stores whose website uses Firebase for push instead of the OneSignal web SDK.
// "*" switches every store. A store is only safe to switch once the Firebase
// sender service account has access to its Firebase project: its web subscribers
// switch to Firebase on their next visit and would stop receiving push otherwise.
// Checked 2026-09-15: every open store's project grants that access.
const FIREBASE_WEB_PUSH_GROUPS: string[] = ["*"];

// Google Cloud project id format. groupApp.webFirebase.projectId has held other
// values (a Realtime Database URL for pizzaexpress); such a store keeps OneSignal.
const FIREBASE_PROJECT_ID_PATTERN = /^[a-z][a-z0-9-]{4,28}[a-z0-9]$/;

// Served from the site root (angular.json assets). Its own scope, so it does not
// replace any other service worker of the site.
const WEB_PUSH_SERVICE_WORKER_URL = "/firebase-messaging-sw.js";
const WEB_PUSH_SERVICE_WORKER_SCOPE = "/firebase-cloud-messaging-push-scope";
// Sent by the service worker when a notification is tapped while a tab is open.
const WEB_PUSH_CLICK_MESSAGE = "dm-web-push-click";

@Injectable({
  providedIn: "root",
})
export class OneSignalService {
  private mobilePushListenersAdded = false;
  private webPushListenersAdded = false;
  private webPushSupported: Promise<boolean> | null = null;
  private androidPushPreferenceSupportPromise: Promise<boolean | null> | null = null;

  constructor(
    private alertController: AlertController,
    private translate: TranslateService,
    private router: Router,
    private store: Store<fromApp.AppState>,
    private toastController: ToastController,
    private firebaseBrowserService: FirebaseBrowserService
  ) {}

  initOneSignalBrowser(groupApp) {
    if (!groupApp || !groupApp.oneSignalAppId || !groupApp.oneSignalAppId.trim()) {
      console.log("onesignal initialization skipped: missing app ID");
      return;
    }

    try {
      window.OneSignal = window.OneSignal || [];

      if (!_.isEmpty(window.OneSignal)) {
        window.OneSignal.push([
          "init",
          {
            path: "/",
            autoRegister: false,
            appId: groupApp.oneSignalAppId,
            safari_web_id: groupApp.safari_web_id,
            notifyButton: {
              enable: false,
            },
          },
        ]);
      }
    } catch (err) {
      console.log("onesignal", err);
      window.OneSignal = null;
    }
  }

  /**
   * Whether this group is set up to send push at all.
   *
   * oneSignalAppId is that signal today - every group currently sending push has
   * one - so it stays the gate until a dedicated flag exists. Registering an FCM
   * token for a group with no push set up would stop its customers falling back
   * to SMS/email, because the backend prefers push whenever a token exists.
   */
  isPushEnabledForGroup(groupApp): boolean {
    return !!(
      groupApp &&
      groupApp.oneSignalAppId &&
      groupApp.oneSignalAppId.trim()
    );
  }

  /**
   * Whether this native app was built with Firebase Cloud Messaging.
   *
   * Decided by what the native build contains, not by the app version: live
   * updates change the JS version, and a build from an older branch can carry a
   * higher version number while still shipping OneSignal. New native builds leave
   * onesignal-cordova-plugin out (includePlugins in capacitor.config.ts), so
   * exactly one of the two push plugins is present in any installed app.
   */
  usesFirebasePush(): boolean {
    return (
      Capacitor.isNativePlatform() &&
      Capacitor.isPluginAvailable("FirebaseMessaging")
    );
  }

  isPushControlDisabled(): Promise<boolean> {
    return this.getAndroidPushPreferenceSupport().then(
      (supported) => supported !== true
    );
  }

  private getAndroidPushPreferenceSupport(): Promise<boolean | null> {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "android") {
      return Promise.resolve(true);
    }

    if (!this.androidPushPreferenceSupportPromise) {
      this.androidPushPreferenceSupportPromise = this.readAndroidPushPreferenceSupport()
        .then((supported) => {
          if (supported === null) {
            this.androidPushPreferenceSupportPromise = null;
          }
          return supported;
        });
    }
    return this.androidPushPreferenceSupportPromise;
  }

  private async readAndroidPushPreferenceSupport(): Promise<boolean | null> {
    let timeout: ReturnType<typeof setTimeout>;
    try {
      // Read the installed APK version, never the live-update bundle version.
      // An unavailable bridge must not block customer initialization or prove
      // that an APK is old: unknown locks the UI but does not reset preferences.
      return await Promise.race([
        App.getInfo().then((info) => supportsAndroidPushPreferences(info?.version)),
        new Promise<null>((resolve) => {
          timeout = setTimeout(() => resolve(null), 2000);
        }),
      ]);
    } catch (error) {
      console.log("push APK version check failed", error);
      return null;
    } finally {
      clearTimeout(timeout);
    }
  }

  async getNativePushPermission(): Promise<boolean | null> {
    let timeout: ReturnType<typeof setTimeout>;
    try {
      return await Promise.race([
        this.readNativePushPermission(),
        new Promise<null>((resolve) => {
          timeout = setTimeout(() => resolve(null), 2000);
        }),
      ]);
    } finally {
      clearTimeout(timeout);
    }
  }

  private async readNativePushPermission(): Promise<boolean | null> {
    if (!Capacitor.isNativePlatform()) {
      return null;
    }

    try {
      if (
        Capacitor.getPlatform() === "android" &&
        Capacitor.isPluginAvailable("LocalNotifications")
      ) {
        // Unlike Firebase 6.x, this also detects app-level blocking below Android 13.
        const permission = await LocalNotifications.checkPermissions();
        return this.pushPermissionGranted(permission.display);
      }
      if (this.usesFirebasePush()) {
        if (Capacitor.getPlatform() === "android") {
          const device = await Device.getInfo();
          if (!device.androidSDKVersion || device.androidSDKVersion < 33) {
            // Older Firebase-only APKs cannot report app-level blocking here.
            return null;
          }
        }
        const permission = await FirebaseMessaging.checkPermissions();
        return this.pushPermissionGranted(permission.receive);
      }
      // Legacy installed apps still use the Cordova bridge. This does not prompt.
      const permission = await OneSignal.Notifications.getPermissionAsync();
      return typeof permission === "boolean" ? permission : null;
    } catch (error) {
      console.log("push permission check failed", error);
      return null;
    }
  }

  private pushPermissionGranted(permission: string): boolean | null {
    if (permission === "granted") {
      return true;
    }
    if (["denied", "prompt", "prompt-with-rationale"].includes(permission)) {
      return false;
    }
    return null;
  }

  async reconcileNativePushPreferences(customer): Promise<boolean> {
    if (!customer || customer.only_visitor || !Capacitor.isNativePlatform()) {
      return false;
    }
    if ((await this.getAndroidPushPreferenceSupport()) === false) {
      return disableUnavailablePushPreferences(customer, false);
    }
    return disableUnavailablePushPreferences(
      customer,
      await this.getNativePushPermission()
    );
  }

  async preparePushPreference(enabled: boolean): Promise<PushPreferenceResult> {
    if (!enabled || !Capacitor.isNativePlatform()) {
      return { enabled: !!enabled };
    }
    if (await this.isPushControlDisabled()) {
      return { enabled: false };
    }

    try {
      if (!this.isPushEnabledForGroup(await this.currentGroupApp())) {
        return { enabled: false };
      }
      if ((await this.OneSignalPrompt()) !== true) {
        return { enabled: false };
      }
      if ((await this.getNativePushPermission()) !== true) {
        return { enabled: false };
      }
      if (this.usesFirebasePush()) {
        const { token } = await FirebaseMessaging.getToken();
        if (!token) {
          return { enabled: false };
        }
        return {
          enabled: true,
          device: {
            fcmToken: token,
            platform: Capacitor.getPlatform(),
            timestamp: Date.now().toString(),
          },
        };
      }
      return { enabled: true };
    } catch (error) {
      console.log("push preference activation failed", error);
      return { enabled: false };
    }
  }

  /**
   * Browser only. Whether this store's website is switched to Firebase push:
   * listed in FIREBASE_WEB_PUSH_GROUPS and with a complete Firebase web config.
   */
  isFirebaseWebPushGroup(groupApp): boolean {
    if (Capacitor.isNativePlatform() || !this.isPushEnabledForGroup(groupApp)) {
      return false;
    }
    const switched =
      FIREBASE_WEB_PUSH_GROUPS.includes("*") ||
      FIREBASE_WEB_PUSH_GROUPS.includes(groupApp.group);
    return switched && !!this.webPushConfig(groupApp);
  }

  /**
   * Browser only. A switched store whose visitor's browser supports Firebase web
   * push. Any other browser keeps the OneSignal web SDK.
   */
  async usesFirebaseWebPush(groupApp): Promise<boolean> {
    if (!this.isFirebaseWebPushGroup(groupApp)) {
      return false;
    }
    if (!("serviceWorker" in navigator) || typeof Notification === "undefined") {
      return false;
    }
    if (!this.webPushSupported) {
      this.webPushSupported = isSupported().catch(() => false);
    }
    return this.webPushSupported;
  }

  /**
   * Browser, Firebase stores. Shows a push that arrives while the site is open
   * (the service worker shows it otherwise) and opens the right page when a
   * notification is tapped while a tab is open.
   */
  initFirebaseWebPush(groupApp) {
    if (this.webPushListenersAdded) {
      return;
    }
    this.webPushListenersAdded = true;

    console.log("menu", "Initialize Firebase Cloud Messaging for the browser");

    navigator.serviceWorker.addEventListener("message", (event) => {
      const data = event ? event.data : null;
      if (
        data &&
        data.type === WEB_PUSH_CLICK_MESSAGE &&
        typeof data.path === "string"
      ) {
        this.router.navigateByUrl(data.path, { replaceUrl: true });
      }
    });

    try {
      onMessage(getMessaging(this.getWebFirebaseApp(groupApp)), async (payload) => {
        const notification = payload ? payload.notification : null;
        if (!notification || (!notification.title && !notification.body)) {
          return;
        }
        try {
          const registration = await this.getWebPushRegistration(groupApp);
          // Same data shape the Firebase service worker uses, so a tap on it is
          // routed by the service worker in the same way.
          await registration.showNotification(notification.title || "", {
            body: notification.body || "",
            data: { FCM_MSG: payload },
          });
        } catch (err) {
          console.log("web push foreground notification err", err);
        }
      });
    } catch (err) {
      this.webPushListenersAdded = false;
      console.log("firebase web messaging err", err);
    }
  }

  /**
   * Browser, Firebase stores. The Firebase push token, or null when the visitor
   * has not allowed notifications. Never prompts.
   */
  async getFirebaseWebPushToken(groupApp): Promise<string | null> {
    if (Notification.permission !== "granted") {
      return null;
    }
    const registration = await this.getWebPushRegistration(groupApp);
    // No vapidKey: the Firebase SDK's default key, so no web push certificate
    // has to be created in each store's Firebase project.
    const token = await getToken(getMessaging(this.getWebFirebaseApp(groupApp)), {
      serviceWorkerRegistration: registration,
    });
    return token || null;
  }

  /**
   * Browser, Firebase stores, once a Firebase token is saved. Removes the old
   * OneSignal web service worker so the browser keeps a single push subscription.
   */
  async removeOneSignalWebWorker() {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        const worker =
          registration.active || registration.waiting || registration.installing;
        if (worker && /OneSignalSDK/i.test(worker.scriptURL)) {
          await registration.unregister();
          console.log("removed the OneSignal web service worker");
        }
      }
    } catch (err) {
      console.log("onesignal web worker cleanup err", err);
    }
  }

  private webPushConfig(groupApp) {
    const config = groupApp ? groupApp.webFirebase : null;
    if (
      !config ||
      !config.apiKey ||
      !config.projectId ||
      !config.messagingSenderId ||
      !config.appId ||
      !FIREBASE_PROJECT_ID_PATTERN.test(String(config.projectId))
    ) {
      return null;
    }
    return {
      apiKey: config.apiKey,
      projectId: config.projectId,
      messagingSenderId: config.messagingSenderId,
      appId: config.appId,
    };
  }

  /**
   * The Firebase app the site already uses for phone login, or a separate one
   * when it has not been created yet.
   */
  private getWebFirebaseApp(groupApp) {
    const existing = this.firebaseBrowserService.getFireBaseApp();
    if (existing) {
      return existing;
    }
    const name = "web-push";
    const app = getApps().find((firebaseApp) => firebaseApp.name === name);
    return app || initializeApp(groupApp.webFirebase, name);
  }

  private async getWebPushRegistration(
    groupApp
  ): Promise<ServiceWorkerRegistration> {
    const query = new URLSearchParams(this.webPushConfig(groupApp) || {}).toString();
    const registration = await navigator.serviceWorker.register(
      `${WEB_PUSH_SERVICE_WORKER_URL}?${query}`,
      { scope: WEB_PUSH_SERVICE_WORKER_SCOPE }
    );
    return this.waitForActiveServiceWorker(registration);
  }

  /**
   * getToken() subscribes through the registration, which fails until its
   * service worker is active; a first visit registers it just before.
   */
  private waitForActiveServiceWorker(
    registration: ServiceWorkerRegistration
  ): Promise<ServiceWorkerRegistration> {
    if (registration.active) {
      return Promise.resolve(registration);
    }
    const worker = registration.installing || registration.waiting;
    if (!worker) {
      return Promise.reject(new Error("web push service worker missing"));
    }
    return new Promise((resolve, reject) => {
      const timer = setTimeout(
        () => reject(new Error("web push service worker did not activate")),
        15000
      );
      worker.addEventListener("statechange", () => {
        if (worker.state === "activated") {
          clearTimeout(timer);
          resolve(registration);
        } else if (worker.state === "redundant") {
          clearTimeout(timer);
          reject(new Error("web push service worker failed to install"));
        }
      });
    });
  }

  /**
   * usesFirebaseWebPush() for the store loaded in the store state, for callers
   * that do not hold groupApp.
   */
  async usesFirebaseWebPushForCurrentStore(): Promise<boolean> {
    return this.usesFirebaseWebPush(await this.currentGroupApp());
  }

  private currentGroupApp(): Promise<any> {
    return new Promise((resolve) => {
      this.store
        .select("groupApp")
        .pipe(first())
        .subscribe((state) => resolve(state ? state.groupApp : null));
    });
  }

  /**
   * Browser, Firebase stores. Asks for permission; once granted, refreshes the
   * user data, which runs saveRegId() and saves the new token.
   */
  private async requestFirebaseWebPushPermission() {
    try {
      const permission = await Notification.requestPermission();
      console.log("User accepted notifications: " + (permission === "granted"));
      if (permission === "granted") {
        this.store.dispatch(
          new AuthActions.SetTriggerGetUserDataTimestamp(Date.now())
        );
      }
    } catch (err) {
      console.log("firebase web push permission err", err);
    }
  }

  initOneSignalMobile(groupApp) {
    if (!this.isPushEnabledForGroup(groupApp)) {
      console.log("push initialization skipped: push not enabled for group");
      return;
    }

    if (!this.usesFirebasePush()) {
      this.initOneSignalNative(groupApp);
      return;
    }

    if (this.mobilePushListenersAdded) {
      return;
    }
    this.mobilePushListenersAdded = true;

    console.log("menu", "Initialize Firebase Cloud Messaging for mobile apps");

    FirebaseMessaging.addListener("notificationActionPerformed", (event) => {
      console.log("notificationActionPerformed: ", event);
      this.pushNotificationPopup(this.toPushPopupPayload(event));
    }).catch((err) => {
      this.mobilePushListenersAdded = false;
      console.log("firebase messaging err", err);
    });

    if (Capacitor.getPlatform() === "android") {
      this.setupAndroidForegroundNotifications(groupApp);
    }
  }

  /**
   * Android only. Firebase does not display a notification that arrives while
   * the app is open (iOS does, through presentationOptions). OneSignal did, so
   * it is shown as a normal phone notification with a local notification, and
   * tapping it goes through the same routing as a tapped push.
   */
  private setupAndroidForegroundNotifications(groupApp) {
    if (!Capacitor.isPluginAvailable("LocalNotifications")) {
      console.log("local notifications plugin not available");
      return;
    }

    this.ensureAndroidPushChannel();

    FirebaseMessaging.addListener("notificationReceived", async (event) => {
      const notification =
        event && event.notification ? event.notification : null;
      if (!notification || (!notification.title && !notification.body)) {
        return;
      }

      try {
        const { isActive } = await App.getState();
        if (!isActive) {
          // In the background Firebase already shows it.
          return;
        }
        await this.ensureAndroidPushChannel();
        await LocalNotifications.schedule({
          notifications: [
            {
              id: Math.floor(Date.now() % 2147483647),
              title: notification.title || "",
              body: notification.body || "",
              smallIcon: "ic_stat_notification",
              iconColor: this.notificationAccentColor(groupApp),
              channelId: ANDROID_PUSH_CHANNEL_ID,
              extra: notification.data || {},
            },
          ],
        });
      } catch (err) {
        console.log("foreground notification err", err);
      }
    }).catch((err) => {
      console.log("firebase messaging err", err);
    });

    LocalNotifications.addListener(
      "localNotificationActionPerformed",
      (action) => {
        const notification =
          action && action.notification ? action.notification : null;
        if (!notification) {
          return;
        }
        this.pushNotificationPopup({
          notification: {
            title: notification.title,
            body: notification.body,
            additionalData: notification.extra || {},
          },
        });
      }
    ).catch((err) => {
      console.log("local notifications err", err);
    });
  }

  /**
   * Creates the channel, or refreshes its name once translations have loaded.
   */
  private async ensureAndroidPushChannel() {
    const key = "orderCustomerNotificationsModal.title";
    const translated = this.translate.instant(key);
    try {
      await LocalNotifications.createChannel({
        id: ANDROID_PUSH_CHANNEL_ID,
        name: translated && translated !== key ? translated : "Orders",
        importance: 4,
        visibility: 1,
      });
    } catch (err) {
      console.log("notification channel err", err);
    }
  }

  private notificationAccentColor(groupApp): string | undefined {
    const color =
      groupApp && groupApp.flattenedIconBackground
        ? String(groupApp.flattenedIconBackground).trim()
        : "";
    return /^#([0-9a-f]{6}|[0-9a-f]{8})$/i.test(color) ? color : undefined;
  }

  /**
   * Apps built before Firebase Cloud Messaging: unchanged OneSignal setup.
   */
  private initOneSignalNative(groupApp) {
    console.log(
      "menu",
      "Initialize OneSignal for mobile apps",
      groupApp.oneSignalAppId
    );
    try {
      //118430501239

      OneSignal.initialize(groupApp.oneSignalAppId);

      OneSignal.Notifications.addEventListener('click',(jsonData) => {
        console.log("notificationOpenedCallback: ", jsonData);
        this.pushNotificationPopup(jsonData);
      });
    } catch (err) {
      console.log("onesignal err", err);
    }
  }

  /**
   * pushNotificationPopup() was written against OneSignal's click event, which
   * carries the custom data as notification.additionalData. FCM carries it as
   * notification.data, so it is mapped onto the same shape and the routing in
   * pushNotificationPopup() stays exactly as it was.
   */
  private toPushPopupPayload(event) {
    const notification = event && event.notification ? event.notification : {};
    return {
      notification: {
        title: notification.title,
        body: notification.body,
        additionalData: notification.data || {},
      },
    };
  }

  /**
   *
   * @param jsonData
   */
  private pushNotificationPopup = (jsonData) => {
    this.store
      .select("auth")
      .pipe(first())
      .subscribe((authState) => {
        if (authState) {
          let currentUser = _.cloneDeep(authState.user);
          var payload;
          if (
            jsonData &&
            jsonData.notification &&
            jsonData.notification.additionalData
          ) {
            payload = jsonData.notification;
          } else if (jsonData && jsonData.payload) {
            payload = jsonData.payload;
          } else if (
            jsonData &&
            jsonData.notification &&
            jsonData.notification.payload
          ) {
            payload = jsonData.notification.payload;
          }
          if (
            payload &&
            payload.additionalData &&
            payload.additionalData.reason === "orderStatusUpdate"
          ) {
            this.presentOneSignalAlert(
              payload.body,
              payload.additionalData.title,
              () => {
                if (this.router.url === "/orders") {
                  let message = _.cloneDeep(payload);
                  message.updatedTimestamp = Date.now();
                  this.store.dispatch(
                    new OneSignalMessagesActions.SetOneSignalMessage(
                      _.cloneDeep(message)
                    )
                  );
                } else {
                  this.router.navigateByUrl("/orders", {
                    replaceUrl: true,
                  });
                }
                // } else {
                // this.presentErrorToast(
                //   this.translate.instant(
                //     "orders.order-item.you-must-be-logged-in-to-see-your-orders"
                //   ),
                //   "danger"
                // );
              }
            );
          } else if (
            payload &&
            payload.additionalData &&
            payload.additionalData.reason === "reviewNotification"
          ) {
            if (this.router.url === "/orders") {
              let message = _.cloneDeep(payload);
              message.updatedTimestamp = Date.now();
              this.store.dispatch(
                new OneSignalMessagesActions.SetOneSignalMessage(
                  _.cloneDeep(message)
                )
              );
            } else {
              this.router.navigateByUrl("/orders?openReviewFirstOrder=true", {
                replaceUrl: true,
              });
            }
          } else if (
            payload &&
            payload.additionalData &&
            payload.additionalData.reason === "reviewAnswer"
          ) {
            let message = _.cloneDeep(payload);
            message.updatedTimestamp = Date.now();
            this.router.navigateByUrl("/ratings", {
              replaceUrl: true,
            });
          } else {
            console.log(jsonData);
          } //This is the alert when the user is notified in order to create a review
        }
      })
      .unsubscribe();
  };

  async OneSignalPrompt() {
    if (await this.isPushControlDisabled()) {
      return false;
    }
    if (this.usesFirebasePush()) {
      // FCM has no separate subscription opt-in/opt-out: the OS permission is
      // the switch, so only the permission prompt remains.
      try {
        const permission = await FirebaseMessaging.requestPermissions();
        console.log(
          "User accepted notifications: " + (permission.receive === "granted")
        );
        return this.pushPermissionGranted(permission.receive);
      } catch (err) {
        console.log("firebase messaging err", err);
      }
    } else if (Capacitor.isNativePlatform()) {
      // Apps built before Firebase Cloud Messaging: unchanged OneSignal prompt.
      try {
        const accepted = await OneSignal.Notifications.requestPermission();
            console.log("User accepted notifications: " + accepted);
            if (accepted) {
              await OneSignal.User.pushSubscription.optIn();

            } else {
              await OneSignal.User.pushSubscription.optOut();
            }
            return accepted;
          
        
      } catch (err) {
        console.log("one signal err", err);
      }
    } else if (await this.usesFirebaseWebPushForCurrentStore()) {
      await this.requestFirebaseWebPushPermission();
    } else if (window.OneSignal) {
      window.OneSignal.push([
        "getNotificationPermission",
        (permission) => {
          console.log("menu", "Site Notification Permission:", permission);
        },
      ]);

      window.OneSignal.push([
        "isPushNotificationsEnabled",
        (enabled) => {
          console.log("isPushNotificationsEnabled", enabled);
          if (!enabled) {
            window.OneSignal.push(() => {
              window.OneSignal.registerForPushNotifications();
              window.OneSignal.push(["setSubscription", true]);
            });
          }
        },
      ]);
    } else {
      console.warn("OneSignalPrompt warning");
    }
  }

  async presentErrorToast(message, color) {
    const toast = await this.toastController.create({
      message: message,
      duration: 5000,
      position: "middle",

      color: color,
      buttons: [
        {
          side: "end",
          icon: "assets/ionicons/close.svg",

          handler: () => {
            console.log("Toast Closed");
          },
        },
      ],
    });
    toast.present();
  }

  async presentOneSignalAlert(message, title, viewHandler) {
    const alert = await this.alertController.create({
      header: title,
      message: message,
      buttons: [
        {
          text: "OK",
          role: "cancel",
          cssClass: "secondary",
          handler: (blah) => {
            console.log("Confirm Cancel");
          },
        },
        {
          text: this.translate.instant("view"),
          handler: viewHandler,
        },
      ],
    });

    await alert.present();
  }
}
